/**
 * Price Helper Functions
 * Shared utilities for price formatting and fetching
 *
 * @package
 */

// Served from Constants::MAX_PRODUCT_QUANTITY so the stepper cannot offer a quantity
// the REST layer would clamp. The literal is only a fallback for an unlocalised page.
export const MAX_QUANTITY = ( typeof atConfigurator !== 'undefined' && atConfigurator.maxQuantity ) || 99;

/**
 * Get price from product data for a given VAT mode.
 *
 * The REST price field is now an object { incVAT, exVAT }; this helper selects
 * the correct amount. Legacy scalar values (ACF / meta) are returned as-is.
 *
 * @param {Object} data    Product data object
 * @param {string} vatMode 'including' (default) or 'excluding'
 * @return {number} Price as float or 0 if not found
 */
export const getPriceFromProduct = ( data, vatMode = 'including' ) => {
	const priceData = data?.price;

	if ( priceData && typeof priceData === 'object' ) {
		const amount = vatMode === 'excluding' ? priceData.exVAT : priceData.incVAT;
		return amount ? parseFloat( amount ) : 0;
	}

	// Legacy scalar fallback (ACF / meta).
	const scalar = data?.acf?.price ||
		data?.meta?._qcom_price?.[ 0 ] ||
		data?.meta?.price?.[ 0 ];

	return scalar !== null && scalar !== undefined ? parseFloat( scalar ) : 0;
};

/**
 * Get the installation time (in hours) for one of a product.
 *
 * Reads the `workshop_hours` REST field exposed on qcom_product, which resolves to the
 * product's own installation hours where set and the vehicle's ERP assembly time
 * otherwise. Returns 0 when absent, non-numeric, or negative.
 *
 * @param {Object} data Product data object
 * @return {number} Installation time in hours, or 0
 */
export const getWorkshopHours = data => {
	const hours = data?.workshop_hours;
	const parsed = typeof hours === 'string' ? parseFloat( hours ) : hours;
	return Number.isFinite( parsed ) && parsed > 0 ? parsed : 0;
};

/**
 * Compute the VAT-adjusted installation cost for a number of hours.
 *
 * The hourly rate is stored excl. VAT; in inc-VAT display mode the cost is grossed
 * up by the tax rate so it stays consistent with the product prices.
 *
 * @param {number} hours      Installation hours
 * @param {number} hourlyRate Hourly labour rate (excl. VAT)
 * @param {number} taxRate    VAT rate as a decimal (e.g. 0.25)
 * @param {string} vatMode    'including' or 'excluding'
 * @return {number} Installation cost
 */
export const getWorkshopCost = ( hours, hourlyRate, taxRate = 0, vatMode = 'including' ) => {
	const multiplier = vatMode === 'excluding' ? 1 : 1 + ( taxRate || 0 );
	return ( hours || 0 ) * ( hourlyRate || 0 ) * multiplier;
};

/**
 * Normalise a quantity to a whole number of at least one.
 *
 * @param {number|string} quantity Raw quantity
 * @return {number} Quantity, floored at 1
 */
export const normaliseQuantity = quantity => {
	const parsed = parseInt( quantity, 10 );
	return Number.isFinite( parsed ) && parsed > 1 ? Math.min( parsed, MAX_QUANTITY ) : 1;
};

/**
 * Break a product line down into its installation hours and total cost.
 *
 * Installation is charged per unit, so both price and labour scale with quantity.
 *
 * @param {Object} data                 Product data object
 * @param {Object} [options]            Line options
 * @param {string} [options.vatMode]    'including' or 'excluding'
 * @param {number} [options.quantity]   How many of the product
 * @param {number} [options.hourlyRate] Hourly labour rate (excl. VAT)
 * @param {number} [options.taxRate]    VAT rate as a decimal (e.g. 0.25)
 * @return {{ installationHours: number, total: number }} Line breakdown
 */
export const getProductLine = ( data, { vatMode = 'including', quantity = 1, hourlyRate = 0, taxRate = 0 } = {} ) => {
	const units = normaliseQuantity( quantity );
	const productCost = getPriceFromProduct( data, vatMode ) * units;
	const installationHours = getWorkshopHours( data ) * units;
	const installationCost = getWorkshopCost( installationHours, hourlyRate, taxRate, vatMode );

	return {
		installationHours,
		total: productCost + installationCost,
	};
};

/**
 * Format price in Swedish Kronor
 *
 * @param {number} price Price to format
 * @return {string} Formatted price string
 */
export const formatPrice = price => {
	if ( ! price || price === 0 ) {
		return '0 kr';
	}
	return new Intl.NumberFormat( 'sv-SE', {
		style: 'currency',
		currency: 'SEK',
		minimumFractionDigits: 0,
		maximumFractionDigits: 0,
	} ).format( price );
};
